import React, { useState, useEffect } from 'react';
import { Table, Button, message, Tag, Modal, Input, Image } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import ManageBanner from './ManageBanner';

const { Search } = Input;

interface BannerData{
    bannerId:string;
    bannerText:string;
    bannerImageName: string;
    isActive:boolean;
    createdBy:string;
    createdDate:string;
    updatedBy:string;
    updatedDate:string;
    imageCategoryName:string;
    imageSubCategoryName:string;
}

const DisplayBanners: React.FC = () => {
  const [bannerData, setData] = useState<BannerData[]>([]);
  const [isAddBannerVisible, setIsAddBannerVisible] = useState(false);
  const [isEditBannerVisible, setIsEditBannerVisible] = useState('');
  const [searchText, setSearchText] = useState('');
  const { instance, accounts } = useMsal();

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        return res.idToken;
      };
    
      useEffect(() => {
        fetchToken();
      }, []);

      useEffect(() => {
        getBannerData();
      }, [isAddBannerVisible]);

      const userId = localStorage.getItem("UserId");

      const getBannerData = async () => {
        try {
          const token = await fetchToken();
          const response = await axios.get(`${API_URL}/api/Banner/GetBanners/`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
          setData(response.data);
          console.log("Banner Data", response.data);
        } catch (error) {
          console.error("Error fetching banner details", error);
        }
      };

      const handleToggleBannerStatus = async (bannerId: string, currentStatus: boolean) => {
        const action = currentStatus ? 'deactivate' : 'activate';
        Modal.confirm({
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} Banner`,
          content: `Are you sure you want to ${action} this banner?`,
          okText: 'Yes',
          cancelText: 'No',
          onOk: async () => {
            try {
              const token = await fetchToken();
              const response = await axios.put(`${API_URL}/api/Banner/UpdateBannerStatus`, 
                null,
              {
                params:{
                  banner_id: bannerId,
                  is_active: !currentStatus,
                  user_id: userId
                },
                headers: {
                  Authorization: `Bearer ${token}`
                }
              });
              if (response.status !== 200) {
                throw new Error('Failed to update banner status');
              }
              message.success(`Banner ${action}d successfully`);
              getBannerData();
            } catch (error) {
              console.error(`Error ${action}ing banner`, error);
              message.error(`Failed to ${action} banner`);
            }
          },
        });
      };

      const columns = [
          {
            title: 'Banner Text',
            dataIndex: 'bannerText',
            key: 'bannerText',
            sorter: (a: BannerData, b: BannerData) => a.bannerText.localeCompare(b.bannerText),
          },
          {
            title: 'Banner Image',
            dataIndex: 'bannerImageName',
            key: 'bannerImageName',
            render: (bannerImageName: string) => (
              bannerImageName ? (
                <Image 
                  src={`images/TopBanner/TopMiddleBanner/${bannerImageName}`} 
                  alt="Banner" 
                  style={{ width: 100, height: 50, objectFit: 'cover', cursor: 'pointer', borderRadius: 8 }}
                  preview={{
                    src: `images/TopBanner/TopMiddleBanner/${bannerImageName}`
                  }}
                />
              ) : 'No Image'
            ),
          },
          {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (text: boolean) => (
              <Tag color={text ? 'green' : 'red'}>
                {text ? "Active" : "InActive"}
              </Tag>
            ),
            sorter: (a: BannerData, b: BannerData) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1),
          },
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: BannerData) => (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  type={record.isActive ? 'default' : 'primary'}
                  danger={record.isActive}
                  onClick={() => handleToggleBannerStatus(record.bannerId, record.isActive)}
                >
                  {record.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            ),
          },
        ];

  const filteredData = bannerData.filter((banner) => {
    const searchLower = searchText.toLowerCase();
    const statusText = banner.isActive ? 'active' : 'inactive';
    return banner.bannerText.toLowerCase().includes(searchLower) ||
           statusText.includes(searchLower);
  });

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
              <Button type="primary" onClick={() => setIsAddBannerVisible(false)} style={{ marginRight: 8, height: 32 }}>
                View Banners
              </Button>
              <Button type="primary" onClick={() => {
                setIsAddBannerVisible(true);
                setIsEditBannerVisible('');
              }} style={{ marginRight: 8, height: 32 }}>
                Add Banners
              </Button>
              <Search
                placeholder="Search banners..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300, marginLeft: 'auto' }}
                size="small"
                allowClear
              />
      </div>
              {isAddBannerVisible ? (<ManageBanner />) : (
                <Table 
                    dataSource={filteredData} 
                    columns={columns} 
                    rowKey="bannerId"
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      defaultPageSize: 10,
                    }}
                  />
              )}
    </div>
  );
};

export default DisplayBanners;